import { ActionReducerMapBuilder, createAction, createReducer } from '@reduxjs/toolkit';
import { TableActions, TableSettings, TableSettingsColumn } from '..';
import { fetchUserSettings } from '../../../features/settings/redux/settingsSlices';
import { distinct, notNull } from '../../../utils';

type TableSlice<Namespace extends string> = {
    [K in Namespace as `${K}Actions`]: TableActions;
} & {
    reducer: ReturnType<typeof createTableReducer>;
};

export const createTableSlice = <Namespace extends string>(
    namespace: Namespace,
    initialData: TableSettings,
    builderCallback?: (builder: ActionReducerMapBuilder<TableSettings>) => void
) => {
    const actions = createTableActions(namespace);
    const reducer = createTableReducer(initialData, actions, builderCallback);

    return {
        [`${namespace}Actions`]: actions,
        reducer
    } as TableSlice<Namespace>;
};

const createTableActions = (namespace: string): TableActions => {
    return {
        sortingChanged: createAction(`@${namespace}/sortingChanged`),
        showTooltipsChanged: createAction(`@${namespace}/showTooltipsChanged`),
        filtersChanged: createAction(`@${namespace}/filtersChanged`),
        columnsReordered: createAction(`@${namespace}/columnsReordered`),
        columnsSelected: createAction(`@${namespace}/columnsSelected`),
        columnsResized: createAction(`@${namespace}/columnsResized`)
    };
};

const createTableReducer = (
    initialData: TableSettings,
    tableActions: TableActions,
    builderCallback?: (builder: ActionReducerMapBuilder<TableSettings>) => void
) =>
    createReducer(initialData, builder => {
        builder
            .addCase(tableActions.sortingChanged, (state, action) => {
                state.sorting = {
                    column: action.payload.sortColumn,
                    direction: action.payload.direction
                };
            })
            .addCase(tableActions.showTooltipsChanged, (state, action) => {
                state.showTooltips = action.payload.showTooltips;
            })
            .addCase(tableActions.filtersChanged, (state, action) => {
                state.filters = action.payload;

                // Truncate long strings
                for (const key in state.filters) {
                    if (Object.prototype.hasOwnProperty.call(state, key)) {
                        const filterValue = state.filters[key] as string;
                        if (filterValue?.length > 40) {
                            state.filters[key] = filterValue.substring(0, 40);
                        }
                    }
                }
            })
            .addCase(tableActions.columnsReordered, (state, { payload }) => {
                const columns = state.columns;
                const { sourceColumn, targetColumn } = payload;
                const sourceColumnIndex = columns.findIndex(c => c.key === sourceColumn);
                const targetColumnIndex = columns.findIndex(c => c.key === targetColumn);
                const reorderedColumns = [...columns];

                reorderedColumns.splice(
                    targetColumnIndex,
                    0,
                    reorderedColumns.splice(sourceColumnIndex, 1)[0]
                );

                state.columns = reorderedColumns;
            })
            .addCase(tableActions.columnsSelected, (state, { payload }) => {
                for (const col of state.columns) {
                    col.hide = payload.indexOf(col.key) === -1;
                }
            })
            .addCase(tableActions.columnsResized, (state, { payload }) => {
                const modifiedColumnIndex = state.columns.findIndex(c => c.key === payload.columnKey);
                state.columns[modifiedColumnIndex].width = payload.width;
            })
            .addCase(fetchUserSettings.success, (state, { payload }) => {
                const tableSettings = payload?.tableSettings.find(ts => ts.table === state.table);
                
                if (tableSettings != null) {
                    const sortedColumn = tableSettings.tableColumnSettings.find(tcs => tcs.isSortedOn);
                    if (sortedColumn != null) {
                        state.sorting.column = sortedColumn.name;
                    }
                    state.sorting.direction = tableSettings.ascSort ? 'asc' : 'desc';

                    // If the column name is a number, it is most likely a dynamic column.
                    // TODO: Dynamic columns should be determined in another way..
                    const dynamicColumns = tableSettings.tableColumnSettings.filter(
                        x =>
                            !isNaN(Number(x.name)) ||
                            x.name.startsWith('inPrice-') ||
                            x.name.startsWith('margin-')
                    );

                    const savedColumns = state.columns
                        .map(c => tableSettings.tableColumnSettings.find(tcs => tcs.name === c.key))
                        .filter(notNull);

                    const savedOrderedColumns: TableSettingsColumn[] = dynamicColumns
                        .concat(savedColumns)
                        .filter(distinct)
                        .sort((a, b) => a.order - b.order)
                        .map(tsc => ({
                            key: tsc.name,
                            hide: !tsc.isVisible,
                            width: tsc.width
                        }));

                    const columnsMissingInBackend = state.columns.filter(
                        c => !savedOrderedColumns.some(soc => soc.key === c.key)
                    );

                    state.columns = [...savedOrderedColumns, ...columnsMissingInBackend];

                    for (const tcs of tableSettings.tableColumnSettings) {
                        if (tcs.filter != null && tcs.filter.length > 0) {
                            state.filters[tcs.name] = tcs.filter;
                        }
                    }

                    state.showTooltips = tableSettings.showTooltips;
                }
            });

        if (builderCallback != null) {
            builderCallback(builder);
        }
    });
